import {Asset} from "../entities/Asset";

export interface ICoordinateHelper {
    getCanvasPoint(asset: Asset, zoomLevel: number): GeneralVector;
}

export class ExactCoordinateHelper implements ICoordinateHelper {
    
    constructor(public pixelsPerMeter: number, public vTagPositionReportingMode: string, public useZoom:boolean, public displacePixels:number) {
    }

    public getCanvasPoint(asset: Asset, zoomLevel: number = 1): GeneralVector {
        if (asset.vTagX == null || asset.vTagY == null) {
            return new GeneralVector(null, null);
        }
        if (this.vTagPositionReportingMode == "EXACT") {
            var pixelsToDisplace = this.displacePixels == null || this.displacePixels < 1 ? 20 : this.displacePixels;
            var last4TagId = asset.vTagID.substring(4, 6);
            var last4Int = parseInt(last4TagId, 16);
            var fractionOf = last4Int / 256;
            var xDispFrac = Math.cos(2 * Math.PI * fractionOf);
            var yDispFrac = Math.sin(2 * Math.PI * fractionOf);
            // Leaflet uses powers of 2 for zoom
            var actualZoom = Math.pow(2.0, zoomLevel);
            var xDisp = this.useZoom ? xDispFrac * pixelsToDisplace / actualZoom : xDispFrac * pixelsToDisplace ;
            var yDisp = this.useZoom ? yDispFrac * pixelsToDisplace / actualZoom : yDispFrac * pixelsToDisplace ;
            var lngOrig = asset.vTagX * this.pixelsPerMeter;
            var latOrig = asset.vTagY * this.pixelsPerMeter;
            return new GeneralVector(lngOrig + xDisp, latOrig + yDisp);
        } else {
            return new GeneralVector(asset.vTagX * this.pixelsPerMeter, asset.vTagY * this.pixelsPerMeter);
        }
    }
}

export class GpsCoordinateHelper implements ICoordinateHelper {
    A: number = 6378137.00;
    B: number = 6356752.31;
    F: number = 1.0 / 298.257223563;
    E_SQUARED: number = 2 * this.F - this.F * this.F;
    // Degrees to radians
    RADIANS_PER_DEGREE: number = Math.PI / 180.0;
    // Small variation for getting scale
    DELTA_DEGREES: number = 0.001;
    // Member variables
    private sr1: ScaledReferencePoint;
    private sr2: ScaledReferencePoint;
    private meters_per_degree_X: number;
    private meters_per_degree_Y: number;
    private pixels_per_meter: number;
    private v_globe: GeneralVector;
    /* Long, Lat Unit Vector */
    private v_map: GeneralVector;

    /* X, Y Pixels Unit Vector */
    constructor(r1: ReferencePoint, r2: ReferencePoint, public userSuppliedPPM:number) {
        // User supplied PPM is only used as a fallback for no GPS fix situation
        // Better to work in meters when transforming 2-D coordinates so we calculate scaling factors - use a small delta change in longitude or latitude
        var p1: EllipsoidPoint = this.generate_point(new GlobePoint(r1.Latitude, r1.Longitude, r1.Altitude));
        var p2: EllipsoidPoint = this.generate_point(new GlobePoint(r1.Latitude, r1.Longitude + this.DELTA_DEGREES, r1.Altitude));
        var p3: EllipsoidPoint = this.generate_point(new GlobePoint(r1.Latitude + this.DELTA_DEGREES, r1.Longitude, r1.Altitude));
        this.meters_per_degree_X = this.distance(p1, p2) / this.DELTA_DEGREES;
        this.meters_per_degree_Y = this.distance(p1, p3) / this.DELTA_DEGREES;
        // Generate new reference points using scaled (long, lat)
        this.sr1 = new ScaledReferencePoint(r1.Longitude * this.meters_per_degree_X, r1.Latitude * this.meters_per_degree_Y, r1.X_0, r1.Y_0);
        this.sr2 = new ScaledReferencePoint(r2.Longitude * this.meters_per_degree_X, r2.Latitude * this.meters_per_degree_Y, r2.X_0, r2.Y_0);
        // Generate unit vectors from the reference points
        this.v_globe = this.generate_unit_vector(this.sr1.ScaledLongitude, this.sr1.ScaledLatitude, this.sr2.ScaledLongitude, this.sr2.ScaledLatitude);
        this.v_map = this.generate_unit_vector(this.sr1.X_0, this.sr1.Y_0, this.sr2.X_0, this.sr2.Y_0);
        this.pixels_per_meter = this.distance_2D(this.sr1.X_0, this.sr1.Y_0, this.sr2.X_0, this.sr2.Y_0) / this.distance_2D(this.sr1.ScaledLongitude, this.sr1.ScaledLatitude, this.sr2.ScaledLongitude, this.sr2.ScaledLatitude);
    }

    // Calculate a unit vector from one point to another
    private generate_unit_vector(x1: number, y1: number, x2: number, y2: number): GeneralVector {
        var x_diff: number = x2 - x1;
        var y_diff: number = y2 - y1;
        var length: number = Math.sqrt(x_diff * x_diff + y_diff * y_diff);
        return new GeneralVector(x_diff / length, y_diff / length);
    }

    // Calculate a distance in 2D
    private distance_2D(x1: number, y1: number, x2: number, y2: number): number {
        var x_diff: number = x2 - x1;
        var y_diff: number = y2 - y1;
        return Math.sqrt(x_diff * x_diff + y_diff * y_diff);
    }

    // Convert GPS coordinates to bitmap coordinates
    public getCanvasPoint(asset: Asset, zoomLevel: number = 1): GeneralVector {
        // No fix - use coordinates of nearest fixed instead
        if (asset.vTagPositionType == "GPS No Fix" && asset.nearestFixed != null) {
            if (asset.vTagX == null || asset.vTagY == null) {
                return new GeneralVector(null, null);
            }
            var last4TagId = asset.vTagID.substring(4, 6);
            var last4Int = parseInt(last4TagId, 16);
            var fractionOf = last4Int / 256;
            var xDispFrac = Math.cos(2 * Math.PI * fractionOf);
            var yDispFrac = Math.sin(2 * Math.PI * fractionOf);
            // Leaflet uses powers of 2 for zoom
            var actualZoom = Math.pow(2.0, zoomLevel);
            var xDisp = xDispFrac * 40 / actualZoom;
            var yDisp = yDispFrac * 40 / actualZoom;
            var lngOrig = asset.vTagX * this.userSuppliedPPM;
            var latOrig = asset.vTagY * this.userSuppliedPPM;
            return new GeneralVector(lngOrig + xDisp, latOrig + yDisp);
        }
        // Check for missing coordinates
        if (asset.longitude == null && asset.latitude == null) {
            return new GeneralVector(null, null);
        }
        // Switch to scaled coordinate system for uniform axes then compute offset relative to reference point
        var latitude = asset.latitude;
        var longitude = asset.longitude;
        var scaled_longitude_offset: number = longitude * this.meters_per_degree_X - this.sr1.ScaledLongitude;
        var scaled_latitude_offset: number = latitude * this.meters_per_degree_Y - this.sr1.ScaledLatitude;
        // Project onto unit vector and perpendicular to unit vector
        var a = this.v_globe.X * scaled_longitude_offset + this.v_globe.Y * scaled_latitude_offset;
        var b = -1.0 * this.v_globe.Y * scaled_longitude_offset + this.v_globe.X * scaled_latitude_offset;
        // Take account of difference scales of coordinate systems
        a *= this.pixels_per_meter;
        b *= this.pixels_per_meter;
        // (Long, Lat) is (Long) on X, (Lat) on Y in Cartesian; Pixels is (Bitmap:X) on X, (Bitmap:Y) on (-Y) in Cartesian;
        // Calculate results using projected values
        var x = this.sr1.X_0 + a * this.v_map.X + b * this.v_map.Y * -1.0;
        var y = this.sr1.Y_0 + a * this.v_map.Y + b * this.v_map.X;
        return new GeneralVector(x, y);
    }

    // Convert GPS coordinates to bitmap coordinates
    public getTrackPoint(latitude: number, longitude: number): GeneralVector {
        // Switch to scaled coordinate system for uniform axes then compute offset relative to reference point
        var scaled_longitude_offset: number = longitude * this.meters_per_degree_X - this.sr1.ScaledLongitude;
        var scaled_latitude_offset: number = latitude * this.meters_per_degree_Y - this.sr1.ScaledLatitude;
        // Project onto unit vector and perpendicular to unit vector
        var a = this.v_globe.X * scaled_longitude_offset + this.v_globe.Y * scaled_latitude_offset;
        var b = -1.0 * this.v_globe.Y * scaled_longitude_offset + this.v_globe.X * scaled_latitude_offset;
        // Take account of difference scales of coordinate systems
        a *= this.pixels_per_meter;
        b *= this.pixels_per_meter;
        // (Long, Lat) is (Long) on X, (Lat) on Y in Cartesian; Pixels is (Bitmap:X) on X, (Bitmap:Y) on (-Y) in Cartesian;
        // Calculate results using projected values
        var x = this.sr1.X_0 + a * this.v_map.X + b * this.v_map.Y * -1.0;
        var y = this.sr1.Y_0 + a * this.v_map.Y + b * this.v_map.X;
        return new GeneralVector(x, y);
    }

    // Convert bitmap coordinates to GPS coordinates
    public pixels_to_degrees(x: number, y: number): GlobePoint {
        // Compute offset relative to reference point
        var x_offset: number = x - this.sr1.X_0;
        var y_offset: number = y - this.sr1.Y_0;
        // Project onto unit vector and perpendicular to unit vector
        var a: number = this.v_map.X * x_offset + this.v_map.Y * y_offset;
        var b: number = -1.0 * this.v_map.Y * x_offset + this.v_map.X * y_offset;
        // Take account of different scales of coordinate systems
        a /= this.pixels_per_meter;
        b /= this.pixels_per_meter;
        // (Long, Lat) is (Long) on X, (Lat) on Y in Cartesian; Pixels is (Bitmap:X) on X, (Bitmap:Y) on (-Y) in Cartesian;
        //b *= -1.0;
        // Calculate results using projected values
        var longitude = (this.sr1.ScaledLongitude + a * this.v_globe.X + b * this.v_globe.Y * -1.0) / this.meters_per_degree_X;
        var latitude = (this.sr1.ScaledLatitude + a * this.v_globe.Y + b * this.v_globe.X) / this.meters_per_degree_Y;
        return new GlobePoint(latitude, longitude, 200);
    }

    // Scale for converting pixels to meters
    public get_meters_per_pixel(): number {
        return 1.0 / this.pixels_per_meter;
    }

    // Convert longitude, latitude, altitude to (X, Y, Z) using equations from the internet
    generate_point(g: GlobePoint): EllipsoidPoint {
        var result: EllipsoidPoint = new EllipsoidPoint();
        var st: number = Math.sin(g.Latitude * this.RADIANS_PER_DEGREE);
        /* Sin(theta) */
        var n: number = this.A / Math.sqrt(1.0 - this.E_SQUARED * st * st);
        result.X = (n + g.Altitude) * Math.cos(g.Latitude * this.RADIANS_PER_DEGREE) * Math.cos(g.Longitude * this.RADIANS_PER_DEGREE);
        result.Y = (n + g.Altitude) * Math.cos(g.Latitude * this.RADIANS_PER_DEGREE) * Math.sin(g.Longitude * this.RADIANS_PER_DEGREE);
        result.Z = ((1 - this.E_SQUARED) * n + g.Altitude) * st;
        return result;
    }

    // Compute distance between points
    distance(p1: EllipsoidPoint, p2: EllipsoidPoint): number {
        var x_diff: number = p1.X - p2.X;
        var y_diff: number = p1.Y - p2.Y;
        var z_diff: number = p1.Z - p2.Z;
        return Math.sqrt(x_diff * x_diff + y_diff * y_diff + z_diff * z_diff);
    }
}

export class ReferencePoint {
    constructor(public Longitude: number, public Latitude: number, public Altitude: number, public X_0: number, public Y_0: number) {

    }
}

export class ScaledReferencePoint {
    constructor(public ScaledLongitude: number, public ScaledLatitude: number, public X_0: number, public Y_0: number) {
    }
}

export class GlobePoint {
    constructor(public Latitude: number, public Longitude: number, public Altitude: number) {
    }
}

export class EllipsoidPoint {
    constructor(public X?: number, public Y?: number, public Z?: number) {
    }
}

export class GeneralVector {
    constructor(public X: number, public Y: number) {
    }
}
